/**
 * Three-state classification of a shift's classification credential check.
 *
 * WHY NOT A BOOLEAN.
 * `shift_feasibility_mv` computes:
 *
 *   sb.min_classification IS NULL
 *     OR roster_role_satisfies(...)
 *     OR ...
 *     AS classification_ok
 *
 * When a shift carries NO classification requirement, `classification_ok` is
 * TRUE for every worker. That is not a pass. It is an unasked question. The
 * board and the assign panel historically read the vacuous true as a green
 * tick — folding "no requirement recorded" into "requirement met".
 *
 * PRODUCTION (Medea Park, live residential tenant, measured 2026-08-03):
 *   - 3,273 active shifts
 *   - 2,387 (73%) carry NO required_classification
 *       of which 251 are RN shifts, 171 EN, 974 PCA
 *   - 966 of the unclassified shifts are still in the FUTURE (still fixable)
 * Compounding it: Medea runs `worker_compliance_enforcement_mode = WARN`, so
 * even a raised finding blocks nothing. This module makes the state VISIBLE
 * and TRUE. It does not enforce. Enforcement is a provider decision.
 *
 * THE THREE STATES (deliberately not PASS / FAIL / N/A):
 *   - REQUIREMENT_MET    — a requirement was recorded AND the worker satisfies it
 *   - REQUIREMENT_ABSENT — no requirement was recorded; the check does not apply
 *   - REQUIREMENT_UNMET  — a requirement was recorded AND the worker does not satisfy it
 *
 * Naming argument:
 *   - "MET" / "UNMET" are about the recorded requirement, not about worker quality
 *     in the abstract. "PASS" would still imply a positive verification happened.
 *   - "ABSENT" is an honest data state. "N/A" or "VACUOUS" either understate the
 *     gap (N/A sounds fine) or use logician jargon no coordinator will own.
 *   - The REQUIREMENT_ prefix keeps these distinct from operational flags
 *     (`role_ok`, leave, overlap) that answer different questions.
 *
 * CARE VS HOTEL SERVICES.
 * ABSENT is the same state on an RN shift and a laundry shift. The difference
 * is whether a classification is *expected*. Care roles — NURSING and
 * PERSONAL_CARE via `classifyRosterService` — are where absence is a data gap
 * (RN and EN both classify as NURSING). Hotel services (laundry, catering,
 * maintenance) need no clinical classification; absence there is expected.
 * Callers distinguish via `isCareRole` on the result. This module never turns
 * ABSENT into a blocker; that would freeze Medea's roster (2,387 shifts).
 *
 * DISPLAY ONLY at the application layer. Does not write, does not change the
 * MV, and does not alter the assignment RPC.
 */

import { classifyRosterService, type RosterServiceFamily } from './roster-service-classification';

/**
 * Care roles for which a clinical/industrial classification is expected on
 * residential demand. Reuses the display-family classifier so free-text
 * service strings (`RN 07.00 - 15.00`, `PCA`, `EN (enrolled nurse)`) land in
 * the same buckets the board already colours. RN and EN both map to NURSING.
 */
const CARE_ROLE_FAMILIES: ReadonlySet<RosterServiceFamily> = new Set(['NURSING', 'PERSONAL_CARE']);

/**
 * Three distinct answers to "did this worker meet the shift's classification
 * requirement?" — never a boolean that folds absent into met.
 */
export type ClassificationRequirementState =
  | 'REQUIREMENT_MET'
  | 'REQUIREMENT_ABSENT'
  | 'REQUIREMENT_UNMET';

export type ClassificationRequirementAssessment = {
  readonly state: ClassificationRequirementState;
  /**
   * True when the shift's service family is NURSING or PERSONAL_CARE.
   * Distinguishes "RN with no requirement" (data gap) from "laundry with no
   * requirement" (expected). Same `state` (`REQUIREMENT_ABSENT`); different
   * operational meaning.
   */
  readonly isCareRole: boolean;
  readonly serviceFamily: RosterServiceFamily;
};

function firstRecordedClassification(
  requiredClassification: string | null | undefined,
  minClassification: string | null | undefined,
): string | null {
  for (const raw of [requiredClassification, minClassification]) {
    if (raw == null) continue;
    const trimmed = String(raw).trim();
    if (trimmed.length > 0) return trimmed;
  }
  return null;
}

/**
 * Classify a shift's classification-credential check into one of three states.
 *
 * When no requirement is recorded, returns REQUIREMENT_ABSENT regardless of
 * `classificationOk`. The feasibility MV sets `classification_ok = true` for
 * every worker in that case; that vacuous true must never become
 * REQUIREMENT_MET.
 */
export function classifyClassificationRequirement(input: {
  /**
   * Shift-level required classification (`roster_shifts.required_classification`
   * or the resolved min from `shift_requirements` / inclusion rules).
   */
  readonly requiredClassification?: string | null;
  /**
   * Alternate column name from the feasibility basis
   * (`shift_requirements.min_classification`). Either field counts as a
   * recorded requirement.
   */
  readonly minClassification?: string | null;
  /**
   * Worker-vs-requirement outcome from the feasibility projection.
   * Only consulted when a requirement is recorded. When the requirement is
   * absent, the MV's vacuous `true` is deliberately ignored.
   *
   * Missing / null with a recorded requirement fails closed to UNMET — same
   * direction as the hard-flag fully-ok path (unknown genuine check is not a pass).
   */
  readonly classificationOk?: boolean | null;
  readonly serviceType?: string | null;
  readonly serviceCode?: string | null;
}): ClassificationRequirementAssessment {
  const serviceFamily = classifyRosterService({
    serviceCode: input.serviceCode,
    serviceType: input.serviceType,
  });
  const isCareRole = CARE_ROLE_FAMILIES.has(serviceFamily);

  const recorded = firstRecordedClassification(
    input.requiredClassification,
    input.minClassification,
  );

  if (recorded == null) {
    // Unasked question. classificationOk is irrelevant — typically true in the MV.
    return {
      state: 'REQUIREMENT_ABSENT',
      isCareRole,
      serviceFamily,
    };
  }

  if (input.classificationOk === true) {
    return {
      state: 'REQUIREMENT_MET',
      isCareRole,
      serviceFamily,
    };
  }

  return {
    state: 'REQUIREMENT_UNMET',
    isCareRole,
    serviceFamily,
  };
}

/**
 * True when the service is a care role that expects a clinical classification.
 * Thin wrapper so callers need not re-derive the NURSING / PERSONAL_CARE set.
 */
export function isClassificationCareRole(input: {
  readonly serviceType?: string | null;
  readonly serviceCode?: string | null;
}): boolean {
  const family = classifyRosterService({
    serviceCode: input.serviceCode,
    serviceType: input.serviceType,
  });
  return CARE_ROLE_FAMILIES.has(family);
}
